import { getChatItems } from "@/store/chat/getChatItems";
import { AppStore } from "@/store/AppStore";
import { ChatMessageContext, ChatItem, ChatItemKind, ChatHistoryIndexes } from "@/store/chat/types";
import { makeObservable, observable, action, computed } from "mobx";
import { Maybe } from "@/domains/common/types";
import { Subscription } from "dexie";
import { Uuid } from "@/domains/global/identifiers";

export class ChatHistory {
  private store: AppStore;

  context?: ChatMessageContext; // TODO (RICK): remove this after guided chat. There will no longer be a "single context" concept to work around.
  items: ChatItem[] = [];
  contextStartedAt?: string;
  liveQuerySubscription: Maybe<Subscription>;
  conversationId?: Uuid;
  isGuidedMode = true;

  constructor({
    indexes,
    context,
    store,
    conversationId,
  }: {
    indexes: ChatHistoryIndexes[];
    context?: ChatMessageContext;
    store: AppStore;
    conversationId?: Uuid;
  }) {
    this.context = context;
    this.store = store;
    this.conversationId = conversationId;

    this.items = getChatItems(indexes);
    const lastSectionHeader = this.items.findLast(item => item.kind === ChatItemKind.SectionHeader);
    if (
      lastSectionHeader &&
      "locallyCreatedAt" in lastSectionHeader &&
      typeof lastSectionHeader.locallyCreatedAt === "string"
    ) {
      this.contextStartedAt = lastSectionHeader.locallyCreatedAt;
    }

    makeObservable<ChatHistory, "store" | "setDraftMessage" | "getDraftMessage">(this, {
      isPrimaryChatConversation: true,
      hasSomeContexts: true,
      isGuidedMode: true,
      conversationId: true,
      liveQuerySubscription: true,
      store: false,
      contextStartedAt: false,
      context: observable,
      items: observable,
      submitChatMessage: action,
      setDraftMessage: false,
      getDraftMessage: false,
      contexts: computed,
    });
  }

  getDraftMessage(): string | undefined {
    if (!this.conversationId) return undefined;
    const msg = this.store.chatMessages.getDraftMessage(this.conversationId);
    return msg;
  }

  get contexts(): ChatMessageContext[] {
    if (this.context) return [this.context];
    if (!this.conversationId) return [];
    return this.store.chatMessages.getChatContextsByConversationId(this.conversationId);
  }

  get hasSomeContexts(): boolean {
    if (!this.conversationId) return false;
    const msgs = this.store.chatMessages.getMessagesInConversation(this.conversationId);
    return msgs.some(msg => msg.hasGuidedContext || msg.hasSources);
  }

  get isPrimaryChatConversation(): boolean {
    return this.conversationId === this.store.chatConversations.primaryChatConversationId;
  }

  setDraftMessage = (message?: string) => {
    if (!this.conversationId) return;
    if (!message) {
      this.store.chatMessages.clearDraftMessage(this.conversationId);
      return;
    }
    // If the message string ends in an empty paragraph tag, remove that
    // This happens when the user presses enter to submit their chat message
    if (typeof message === "string") {
      // Simple regex-based cleanup for trailing empty paragraph
      message = message.replace(/(<p>\s*<\/p>\s*)$/i, "");
    }
    this.store.chatMessages.setDraftMessage(this.conversationId, message);
  };

  submitChatMessage = async ({
    markdownContent,
    isGuidedChat_experiment,
    agentMode,
    contexts: providedContexts,
  }: {
    markdownContent: string;
    isGuidedChat_experiment: boolean;
    agentMode: boolean;
    contexts?: ChatMessageContext[];
  }): Promise<boolean> => {
    console.log("[CHAT] Chat History ID is ", this.conversationId);

    if (!markdownContent.trim()) return false;

    const previousDraftMessage = this.getDraftMessage();
    this.setDraftMessage(undefined);
    const contexts: ChatMessageContext[] =
      providedContexts ?? (this.context !== undefined ? [this.context] : []);

    const didSend = await this.store.chatMessages.sendNewMessage({
      message: markdownContent,
      contexts,
      contextStartedAt: this.contextStartedAt,
      conversationId: this.conversationId,
      isGuidedChat_experiment,
      agentMode,
    });

    // If the message was not sent (due to being over the limit for their plan),
    // restore the previous draft message
    if (!didSend) {
      this.setDraftMessage(previousDraftMessage);
    }

    return didSend;
  };
}
